import { Component, DestroyRef, ElementRef, inject, OnInit, Renderer2, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { withCommunity } from 'src/app/core/services/co-brand.service';
import { ActivatedRoute, NavigationEnd, Router, RouterLinkActive, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from 'src/app/shared/services/common.service';
import { BorrowLendService } from '../../services/borrow-lend.service';
import { WebStorageService } from '../../services/web-storage.service';
import { HeightService } from '../../services/height.service';
import { environment } from 'src/environments/environment';
import { filter } from 'rxjs';
import { canAccessVerifiedUserApis } from '../../Helper/common-utils';
import { NgClass } from '@angular/common';
import { WalletLabelPipe } from '../../pipes/wallet-label.pipe';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.css'],
    imports: [RouterLinkActive, RouterLink, NgClass, WalletLabelPipe]
})
export class SidebarComponent implements OnInit {
  @ViewChild('announcementContainer', { static: false }) announcementContainer: ElementRef | undefined;
  dynamicHeight: number = 0;
  isGridView!: boolean;
  showoverlay: boolean = false;
  clickedTabName!: string;
  user: any;
  regulated: boolean = false;
  announceData = { enabled: true };
  isExpand: boolean = false;
  anotherContainer: any;
  whatsappUrl =`https://wa.me/${environment.WHATSAPP_NUMBER}?text=${encodeURIComponent(environment.WHATSAPP_MESSAGE)}`;
  isWalletActive = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private commonService: CommonService,
    private webStorageService: WebStorageService,
    private borrowLendService: BorrowLendService,
    private toastr: ToastrService,
    private router: Router,
    private renderer: Renderer2,
    private heightService: HeightService,
    private activatedRoute: ActivatedRoute,
  ) { }

  ngOnInit() {
    this.readSession();
    // this.gridView(JSON.parse(this.webStorageService.getLocalStorage('gridView') || 'true'));
    this.commonService.tabEmitterObserve.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((clickedTab) => {
      if (Object.keys(clickedTab).length > 0) {
        this.clickedTabName = clickedTab.type;
      }
    })
    this.commonService.showmodaloverlayObservable.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: boolean) => this.showoverlay = response)
    const storedView = localStorage.getItem('gridView');
    this.isGridView = storedView !== null ? storedView === 'true' : true;
    this.updateActiveState();

    // The sidebar now lives across page changes (AppRouteReuseStrategy keeps the layout), so
    // the session is re-read here rather than only when the sidebar is created: a KYC that
    // completes, or a switch between custodial and self-custody, applies from the next page.
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.readSession();
        this.updateActiveState();
      });
  }

  /** The signed-in user and custody mode, as the rest of the app stored them. */
  private readSession(): void {
    this.user = this.webStorageService.getLocalStorage('user') != null ? JSON.parse(this.webStorageService.getLocalStorage('user') || 'undefined') : this.user;
    this.regulated = JSON.parse(this.webStorageService.getLocalStorage('regulated') || 'true');
  }

  private updateActiveState() {
    const url = this.router.url;
    // this.isWalletActive = url.startsWith('/my-wallet') || url.startsWith('/redeem-asset');
    // Deposits ("Deposit asset." header button + Wallet's Deposits tab) live under Wallet too.
    this.isWalletActive = url.startsWith('/my-wallet') || url.startsWith('/redeem-asset')
      || url.startsWith('/my-deposits') || url.startsWith('/deposit-asset') || url.startsWith('/onboardassets');
    // Clear the previous menu's highlight so e.g. Borrow doesn't stay active alongside Wallet.
    this.clickedTabName = this.isWalletActive ? '' : (this.getActiveMenuFromUrl(url) || this.clickedTabName);
  }

  private getActiveMenuFromUrl(url: string): string {
    if (url.startsWith('/dashboard')) return 'dashboard';
    if (url.startsWith('/borrow') || url.startsWith('/borrow-history') || url.startsWith('/loan-request') || url.startsWith('/borrow-detail')) return 'borrow';
    if (url.startsWith('/lend') || url.startsWith('/lending-history') || url.startsWith('/lending-detail') || url.startsWith('/offer-loans') || url.startsWith('/resell-loan')) return 'lend';
    if (url.startsWith('/all-assets') || url.startsWith('/nft-detail') || url.startsWith('/collection-detail')) return 'all-items';
    return '';
  }
  
  closeOveraly() {
    this.showoverlay = false;
    this.commonService.setOverlay(false)
  }


/**
 * Switches the view between grid and list.
 * If ctrl/meta key is pressed while clicking, it will open the current page in a new tab with the opposite view.
 * @param event the mouse event
 * @param isGridView boolean indicating whether the view should be switched to grid or list
 */
  public gridView(event: MouseEvent,isGridView: boolean) {
    if (event.ctrlKey || event.metaKey) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([], {
          relativeTo: this.activatedRoute,
          queryParams: { view: isGridView ? 'grid' : 'list' },
          queryParamsHandling: 'merge'
        })
      );
    
      // keeps ?community_id= in the new tab
      window.open(withCommunity(url), '_blank');
      return;
    }

    this.isGridView = isGridView;
    this.commonService.setView(isGridView);
    this.webStorageService.setLocalStorage('gridView', isGridView)
  }

  onMenuClick(menu: string) {
    if (menu === 'all-items') {
      sessionStorage.removeItem('nftDetailReturnUrl');
      sessionStorage.removeItem('dashboardFilterRestore');
      sessionStorage.removeItem('dashboardFilterCategory');
      sessionStorage.setItem('filters', '[]');
      this.commonService.setTabEmitter({ type: 'all-items', action: 'reset' });
      if (!this.router.url.startsWith('/all-assets?') && this.router.url !== '/all-assets') {
        this.router.navigate(['/all-assets']);
      }
      return;
    }
    this.commonService.setTabEmitter({ type: menu });
  }

  redirectToLend() {
    // Navigate immediately for instant route transition
    this.router.navigate(['/lend']);
    
    // Optionally check status and redirect if needed (non-blocking)
    if (this.user?._id && canAccessVerifiedUserApis(this.user, this.regulated)) {
      this.borrowLendService.getUserLendingStatus().subscribe({
        next: async (res: any) => {
          // Only redirect if we're still on the lend page and status indicates different route
          if (res.data?.is_lender_activity && this.router.url === '/lend') {
            this.router.navigate(['/lending-history']);
          }
        },
        error: (error: any) => {
          // Stay on /lend if API call fails
        }
      })
    }
  }

  redirectToBorrow() {
    // Navigate immediately to default route for instant transition
    this.router.navigate(['/borrow']);
    
    // Optionally check status and redirect if needed (non-blocking)
    if (this.user?._id && canAccessVerifiedUserApis(this.user, this.regulated)) {
      this.borrowLendService.getUserBorrowStatus().subscribe({
        next: async (res: any) => {
          // Only redirect if we're still on the borrow page and status indicates loan-request
          if (!res.data?.hasLiveLoans && this.router.url === '/borrow') {
            this.router.navigate(['/loan-request']);
          }
        },
        error: (error: any) => {
          // Stay on /borrow if API call fails
        }
      })
    }
  }

  ngAfterViewInit() {
    this.heightService.headerHeight$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(headerHeight => {
      const normalOpen = document.querySelector('.navbar-collapse.show') !== null ||
                         document.querySelector('.sidebar-collapse.show') !== null;
      const addOpen = document.querySelector('.navbar-collapse.add.show') !== null;
  
      let fixedOffset: number;
  
      if (window.innerWidth <= 575) {
        if (addOpen) {
          fixedOffset = 100; // mobile special case for .add.show
        } else {
          fixedOffset = normalOpen ? 150 : 57; // normal mobile
        }
      } 
      else if (window.innerWidth <= 767) {
        if (addOpen) {
          fixedOffset = 100; 
        } else {
          fixedOffset = normalOpen ? 150 : 57; 
        }
      } 
      else if (window.innerWidth <= 991) {
        if (addOpen) {
          fixedOffset = 100; 
        } else {
          fixedOffset = normalOpen ? 150 : 59; 
        }
      } 
      else if (window.innerWidth <= 1199) {
        if (addOpen) {
          fixedOffset = 100; // tablet special case
        } else {
          fixedOffset = normalOpen ? 100 : 65; // normal tablet
        }
      } 
      else {
        fixedOffset = 60; // desktop
      }
  
      const dynamicTopValue = headerHeight + fixedOffset;
      const element = document.querySelector('.dynamictopheight') as HTMLElement;
      if (element) {
        this.renderer.setStyle(element, 'top', `${dynamicTopValue}px`);
      }
    });
  }
  



}
